"use client";

import { Check, Copy } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import { cn } from "@/lib/utils";

interface CodeBlockProps {
  value: string;
  copyLabel: string;
  title?: string;
  className?: string;
}

function CopyIcon({ copied }: { copied: boolean }) {
  return copied ? (
    <Check className="h-4 w-4 text-success-strong" />
  ) : (
    <Copy className="h-4 w-4" />
  );
}

export function CodeBlock({ value, copyLabel, title, className }: CodeBlockProps) {
  const { copy, copied } = useCopyToClipboard();
  const copyValue = () => void copy(value, `${copyLabel} copied`);
  if (title) {
    return (
      <div className={cn("overflow-hidden rounded-sm border", className)}>
        <div className="flex items-center justify-between gap-3 border-b bg-surface-2 py-1 pl-3 pr-1">
          <span className="truncate text-xs font-medium text-fg-muted">{title}</span>
          <Button
            variant="ghost"
            size="sm"
            className="max-sm:min-h-11"
            aria-label={`Copy ${copyLabel}`}
            onClick={copyValue}
          >
            <CopyIcon copied={copied} />
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <pre className="overflow-x-auto bg-surface-2 p-3 font-mono text-xs leading-relaxed">
          {value}
        </pre>
      </div>
    );
  }
  return (
    <div className={cn("relative", className)}>
      <pre className="overflow-x-auto rounded-sm border bg-surface-2 p-3 pr-12 font-mono text-xs leading-relaxed">
        {value}
      </pre>
      <Button
        variant="ghost"
        size="icon-sm"
        className="absolute right-1.5 top-1.5"
        aria-label={copyLabel}
        onClick={copyValue}
      >
        <CopyIcon copied={copied} />
      </Button>
    </div>
  );
}
